import * as React from "react";
import { Button, Text } from "@uikit";
import { navigate, useStaticQuery, graphql, Link } from "gatsby";

import supabase from "@clients/supabase";
import Textbox from "@uikit/textbox";
import Password from "@uikit/password";
import Phone from "@uikit/phone";
import Choice from "@uikit/choice";
import COUNTRIES from "../../../constants/countries";
import { useApp } from "@store/app";
import { useAuth } from "@store/auth";
import { useWindow } from "@hooks/useWindow";
import { invoke } from "@kalabo/golfsavers-common";
import { ProviderT } from "@models/common";
import Checkbox from "@uikit/checkbox";
import { getMailingListTags, isRegionalSite } from "@utilities/core";
import { notification } from "antd";
import { PhoneNumberUtil } from "google-libphonenumber";

const phoneUtil = PhoneNumberUtil.getInstance();

interface IProps {
  mode: ProviderT;
  redirectUrl: string;
  onSignIn?: () => void;
  onModeChange?: (mode: ProviderT) => void;
}

const AuthUI = ({
  mode = "home",
  redirectUrl,
  onSignIn,
  onModeChange,
}: IProps) => {
  const display_currency = useApp(($) => $.display_currency);
  const hideModals = useApp(($) => $.hideModals);
  const setCurrency = useApp(($) => $.setCurrency);
  const login = useAuth(($) => $.login);
  const signup = useAuth(($) => $.signup);
  const setLoading = useAuth(($) => $.setLoading);
  const error = useAuth(($) => $.error);
  const clearError = useAuth(($) => $.clearError);
  const loading = useAuth(($) => $.loading);
  const [dirty, setDirty] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [join_mailing_list, setJoinMailingList] = React.useState(true);
  const [country_of_residence, setCountryOfResidence] = React.useState("");
  const [country_of_residenceError, setCountryOfResidenceError] =
    React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [first_name, setFirstName] = React.useState("");
  const [first_nameError, setFirstNameError] = React.useState("");
  const [last_name, setLastName] = React.useState("");
  const [last_nameError, setLastNameError] = React.useState("");
  const [phone_number, setPhoneNumber] = React.useState("");
  const [phone_numberError, setPhoneNumberError] = React.useState("");
  const win = useWindow();

  const data = useStaticQuery(graphql`
    query {
      allGolfsaversCurrencies(sort: { name: DESC }) {
        edges {
          node {
            name
            id
            symbol
            code
          }
        }
      }
    }
  `);

  let currencies = data.allGolfsaversCurrencies.edges;

  React.useEffect(() => {
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN") {
        handlePostLogin(session);
      }
    });
  }, []);

  const handlePostLogin = async (session: any) => {
    await handlePostLoginSetup(session?.user?.id);
    onSignIn();
  };

  const handlePostLoginSetup = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_profiles")
      .select(`*`)
      .eq("id", userId)
      .single();

    // get the user's currency from the currencies list
    let currency = currencies?.find(
      (c: any) => c.node.code === data?.display_currency
    ).node;

    setCurrency(currency);
  };

  const checkEmailExistsAndProvider = async (email: string) => {
    let response = await invoke("checkAccountExists", {
      email_address: email,
    });
    if (response.status !== 200) {
      return;
    }

    return response.body;
  };

  const isValidEmail = React.useMemo(() => {
    if (!email) return false;
    // use regex to check if email is valid
    const regex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (regex.test(email)) {
      return true;
    }
    return false;
  }, [email]);

  const isValidPhone = React.useMemo(() => {
    if (!phone_number) return false;

    try {
      let result = phoneUtil.isValidNumber(
        phoneUtil.parseAndKeepRawInput(phone_number)
      );
      return result;
    } catch (error) {
      return false;
    }
  }, [phone_number]);

  const isValidPassword = React.useMemo(() => {
    const regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,40}$/;
    if (!regex.test(password)) return false;
    return true;
  }, [password]);

  const isForgotValid = React.useMemo(() => {
    if (!isValidEmail) return false;
    return true;
  }, [isValidEmail]);

  const isCheckoutValid = React.useMemo(() => {
    if (!isValidEmail) return false;

    return true;
  }, [isValidEmail]);

  const isLoginValid = React.useMemo(() => {
    if (!isValidEmail) return false;
    if (!password) return false;

    return true;
  }, [isValidEmail, password, isValidPassword]);

  const validateForm = React.useCallback(() => {
    if (!isValidEmail) {
      setEmailError("Please enter a valid email");
    } else {
      setEmailError("");
    }

    if (!password) {
      setPasswordError("Please enter a password");
    } else {
      setPasswordError("");
    }

    if (!first_name) {
      setFirstNameError("Please enter your first name");
    } else {
      setFirstNameError("");
    }

    if (!last_name) {
      setLastNameError("Please enter your last name");
    } else {
      setLastNameError("");
    }

    if (!isValidPassword) {
      setPasswordError("Please enter a valid password");
    } else {
      setPasswordError("");
    }

    if (!country_of_residence) {
      setCountryOfResidenceError("Please select your country of residence");
    } else {
      setCountryOfResidenceError("");
    }

    if (!isValidPhone) {
      setPhoneNumberError(
        "Please enter a valid phone number. Do not include a 0 at the start of your number"
      );
    } else {
      setPhoneNumberError("");
    }
  }, [
    isValidPhone,
    isValidEmail,
    email,
    password,
    first_name,
    last_name,
    phone_number,
    country_of_residence,
  ]);

  const validateSignForm = React.useCallback(() => {
    if (!isValidEmail || !email) {
      setEmailError("Please enter a valid email");
    } else {
      setEmailError("");
    }
  }, [isValidEmail, email]);

  const isSignupValid = React.useMemo(() => {
    if (!isValidEmail) return false;
    if (!password) return false;
    if (!first_name) return false;
    if (!last_name) return false;
    if (!isValidPassword) return false;
    if (!isValidPhone) return false;
    if (!country_of_residence) return false;

    if (!dirty) {
      setDirty(true);
    }
    return true;
  }, [
    email,
    password,
    dirty,
    first_name,
    last_name,
    phone_number,
    isValidPassword,
    isValidPhone,
    isValidEmail,
    country_of_residence,
  ]);

  const getRedirectUrl = React.useMemo(() => {
    if (!win) return;
    switch (mode) {
      case "checkout":
        return `${win.location.origin}/checkout`;
      case "manage":
        return `${win.location.origin}/account/bookings`;
      default:
        // return the current full URL
        return `${win.location.origin}/authredirect`;
    }
  }, [redirectUrl, win]);

  const getProvider = (providers: string[]) => {
    if (!providers) return null;
    if (providers.length === 1 && providers[0] === "email") {
      return "email" as ProviderT;
    }
    if (providers.includes("google")) {
      return "google" as ProviderT;
    }
    if (providers.includes("facebook")) {
      return "facebook" as ProviderT;
    }
    if (providers.includes("linkedin_oidc")) {
      return "linkedin" as ProviderT;
    }
    return null;
  };

  const renderMode = () => {
    switch (mode) {
      case "google":
        return (
          <>
            <div className="flex flex-col gap-[10px]">
              <Text
                size={15}
                element="p"
                leading={1.5}
                color="black90"
                className="mb-2"
              >
                You previously signed up using your Google account. Please click
                the button below to log in.
              </Text>
              <div
                onClick={async () => {
                  clearError();
                  supabase.auth.signInWithOAuth({
                    provider: "google",
                    options: {
                      redirectTo: getRedirectUrl,
                    },
                  });
                }}
                className="flex items-center justify-center cursor-pointer bg-white border border-grey3 rounded-md shadow-sm p-[10px] font-semibold gap-[10px] text-black/90 text-17"
              >
                <svg
                  className="w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  width="705.6"
                  height="720"
                  viewBox="0 0 186.69 190.5"
                >
                  <g transform="translate(1184.583 765.171)">
                    <path
                      clipPath="none"
                      mask="none"
                      d="M-1089.333-687.239v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z"
                      fill="#4285f4"
                    />
                    <path
                      clipPath="none"
                      mask="none"
                      d="M-1142.714-651.791l-6.972 5.337-24.679 19.223h0c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z"
                      fill="#34a853"
                    />
                    <path
                      clipPath="none"
                      mask="none"
                      d="M-1174.365-712.61c-6.494 12.815-10.217 27.276-10.217 42.689s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z"
                      fill="#fbbc05"
                    />
                    <path
                      d="M-1089.333-727.244c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276c-16.539-15.413-38.013-24.852-63.731-24.852-37.234 0-69.359 21.388-85.032 52.561l31.692 24.592c7.533-22.514 28.575-39.226 53.34-39.226z"
                      fill="#ea4335"
                      clipPath="none"
                      mask="none"
                    />
                  </g>
                </svg>
                Continue with Google
              </div>
            </div>
          </>
        );
      case "facebook":
        return (
          <>
            <Text
              size={15}
              element="p"
              leading={1.5}
              color="black90"
              className="mb-2"
            >
              You previously signed up using your Facebook account. Please click
              the button below to log in.
            </Text>
            <div className="flex flex-col gap-[10px]">
              <div
                onClick={() => {
                  supabase.auth.signInWithOAuth({
                    provider: "facebook",
                    options: {
                      redirectTo: getRedirectUrl,
                    },
                  });
                }}
                className="flex items-center justify-center cursor-pointer bg-white border border-grey3 rounded-md shadow-sm p-[10px] font-semibold gap-[10px] text-black/90 text-17"
              >
                <svg
                  className="w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  width="1365.12"
                  height="1365.12"
                  viewBox="0 0 14222 14222"
                >
                  <circle cx="7111" cy="7112" r="7111" fill="#1977f3" />
                  <path
                    d="M9879 9168l315-2056H8222V5778c0-562 275-1111 1159-1111h897V2917s-814-139-1592-139c-1624 0-2686 984-2686 2767v1567H4194v2056h1806v4969c362 57 733 86 1111 86s749-30 1111-86V9168z"
                    fill="#fff"
                  />
                </svg>
                Continue with Facebook
              </div>
            </div>
          </>
        );
      case "linkedin":
        return (
          <div className="flex flex-col gap-[10px]">
            {" "}
            <Text
              size={15}
              element="p"
              leading={1.5}
              color="black90"
              className="mb-2"
            >
              You previously signed up using your LinkedIn account. Please click
              the button below to log in.
            </Text>
            <div
              onClick={() => {
                supabase.auth.signInWithOAuth({
                  provider: "linkedin_oidc",
                  options: {
                    redirectTo: getRedirectUrl,
                  },
                });
              }}
              className="flex items-center justify-center cursor-pointer bg-white border border-grey3 rounded-md shadow-sm p-[10px] font-semibold gap-[10px] text-black/90 text-17"
            >
              <svg
                className="w-6 h-6"
                height="72"
                viewBox="0 0 72 72"
                width="72"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g fill="none" fillRule="evenodd">
                  <path
                    d="M8,72 L64,72 C68.418278,72 72,68.418278 72,64 L72,8 C72,3.581722 68.418278,-8.11624501e-16 64,0 L8,0 C3.581722,8.11624501e-16 -5.41083001e-16,3.581722 0,8 L0,64 C5.41083001e-16,68.418278 3.581722,72 8,72 Z"
                    fill="#007EBB"
                  />
                  <path
                    d="M62,62 L51.315625,62 L51.315625,43.8021149 C51.315625,38.8127542 49.4197917,36.0245323 45.4707031,36.0245323 C41.1746094,36.0245323 38.9300781,38.9261103 38.9300781,43.8021149 L38.9300781,62 L28.6333333,62 L28.6333333,27.3333333 L38.9300781,27.3333333 L38.9300781,32.0029283 C38.9300781,32.0029283 42.0260417,26.2742151 49.3825521,26.2742151 C56.7356771,26.2742151 62,30.7644705 62,40.051212 L62,62 Z M16.349349,22.7940133 C12.8420573,22.7940133 10,19.9296567 10,16.3970067 C10,12.8643566 12.8420573,10 16.349349,10 C19.8566406,10 22.6970052,12.8643566 22.6970052,16.3970067 C22.6970052,19.9296567 19.8566406,22.7940133 16.349349,22.7940133 Z M11.0325521,62 L21.769401,62 L21.769401,27.3333333 L11.0325521,27.3333333 L11.0325521,62 Z"
                    fill="#FFF"
                  />
                </g>
              </svg>
              Continue with LinkedIn
            </div>
          </div>
        );

      case "login":
        return (
          <>
            <div className="mt-1 flex flex-col flex-1 gap-4">
              <Textbox
                id="email"
                type="email"
                value={email}
                error={emailError}
                placeholder="Email Address"
                onChange={(e) => setEmail(e)}
                onBlur={() => {
                  if (!email) {
                    setEmailError("Please enter your email address");
                  } else if (!isValidEmail) {
                    setEmailError("Please enter a valid email address");
                  } else {
                    setEmailError("");
                  }
                }}
              />

              <Password
                id="password"
                value={password}
                placeholder="Enter your password"
                onChange={(e) => setPassword(e)}
              />

              <Button
                onClick={async () => {
                  login({ email, password });
                }}
                size="large"
                disabled={!isLoginValid || loading}
                color="green"
                width="full"
                text="Log in"
                className="!m-0"
              />
              <div
                onClick={() => {
                  onModeChange("forgot");
                }}
              >
                <Text
                  size={15}
                  element="p"
                  leading={1.5}
                  color="black90"
                  weight="semibold"
                  className="underline cursor-pointer text-center"
                >
                  Forgot Password{" "}
                </Text>
              </div>
            </div>{" "}
            {error && (
              <div className="mt-2 bg-red/10 border border-red/40 p-4">
                <Text
                  size={15}
                  element="p"
                  leading={1}
                  color="red"
                  weight="semibold"
                  className="text-center m-0"
                >
                  {error?.message || "An error occurred. Please try again."}
                </Text>
              </div>
            )}
          </>
        );
      case "signup":
        return (
          <>
            <div className="mt-1 flex flex-col flex-1 gap-2">
              <Textbox
                id="first_name"
                autoFocus
                error={first_nameError}
                value={first_name}
                placeholder="First name"
                onChange={(e) => setFirstName(e)}
                onBlur={() => {
                  if (!first_name) {
                    setFirstNameError("Please enter your first name");
                  } else {
                    setFirstNameError("");
                  }
                }}
              />

              <Textbox
                id="last_name"
                value={last_name}
                error={last_nameError}
                placeholder="Last name"
                onChange={(e) => setLastName(e)}
                onBlur={() => {
                  if (!last_name) {
                    setLastNameError("Please enter your last name");
                  } else {
                    setLastNameError("");
                  }
                }}
              />

              <Choice
                id="country_of_residence"
                error={country_of_residenceError}
                placeholder="Country of residence"
                searchable
                value={country_of_residence || null}
                options={COUNTRIES?.map((country: any) => {
                  return {
                    label: country.text,
                    value: country.value,
                  };
                })}
                onChange={(e) => {
                  if (!e) {
                    setCountryOfResidenceError(
                      "Please enter your country of residence"
                    );
                  } else {
                    setCountryOfResidenceError("");
                  }

                  // get the country code (phone) from the country value
                  let country = COUNTRIES.find((c: any) => c.value === e);
                  setPhoneNumber(country.dial_code);
                  setCountryOfResidence(String(e));
                }}
              />

              <Textbox
                id="email"
                value={email}
                type="email"
                error={emailError}
                placeholder="Email Address"
                onChange={(e) => setEmail(e)}
                onBlur={() => {
                  if (!email) {
                    setEmailError("Please enter your email address");
                  } else if (!isValidEmail) {
                    setEmailError("Please enter a valid email address");
                  } else {
                    setEmailError("");
                  }
                }}
              />

              <Phone
                id="phone"
                error={phone_numberError}
                value={phone_number}
                placeholder="Phone number"
                onChange={(e) => {
                  if (!e) {
                    setPhoneNumberError(
                      "Please enter a valid phone number. Do not include a 0 at the start of your number"
                    );
                  } else {
                    setPhoneNumberError("");
                  }
                  setPhoneNumber(e);
                }}
              />

              <div>
                <Password
                  id="password"
                  error={passwordError}
                  value={password}
                  placeholder="Create a password"
                  onChange={(e) => setPassword(e)}
                  onBlur={() => {
                    if (!password) {
                      setPasswordError("Please enter your password");
                    } else if (!isValidPassword) {
                      setPasswordError(
                        "Passwords must be at least 8 characters long and contain one uppercase letter and one number."
                      );
                    } else {
                      setPasswordError("");
                    }
                  }}
                />
                {!passwordError && (
                  <Text
                    size={11}
                    color="black60"
                    leading={1.3}
                    className="mt-1"
                  >
                    Passwords must be at least 8 characters long and contain one
                    uppercase letter and one number.
                  </Text>
                )}
              </div>

              <div className="py-2">
                <Checkbox
                  id="join_mailing_list"
                  mode="small"
                  label="Join our mailing list to get the best golf deals in your inbox"
                  horizontallyCentered={false}
                  onChange={(e) => setJoinMailingList(e)}
                  value={join_mailing_list}
                />
              </div>

              <Button
                onClick={async () => {
                  validateForm();
                  if (!isSignupValid) return;
                  setLoading(true);
                  await signup({
                    email,
                    password,
                    first_name,
                    last_name,
                    phone_number,
                    country_of_residence,
                    join_mailing_list,
                    display_currency: display_currency.code,
                  });

                  if (join_mailing_list) {
                    await fetch(
                      "https://zuphopurmhhmbxukmffg.supabase.co/functions/v1/add-to-mailinglist",
                      {
                        method: "POST",
                        headers: {
                          Accept: "application.json",
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          email: email,
                          tags: getMailingListTags(),
                        }),
                      }
                    );
                  }

                  notification.success({
                    message: (
                      <Text
                        size={15}
                        sizeDesktop={17}
                        sizeTablet={17}
                        leading={1.3}
                        weight="semibold"
                        color="black"
                      >
                        Success
                      </Text>
                    ),
                    description: "Your account has been created.",
                    duration: 3,
                  });
                  setLoading(false);
                }}
                loading={loading}
                size="large"
                color="green"
                width="full"
                text="Sign up"
                className="!m-0"
              />
            </div>

            <div className="mt-2 flex flex-col flex-1 gap-4">
              <Text
                size={11}
                color="black90"
                leading={1.3}
                className="text-center"
              >
                We'll never share your information with third parties.
              </Text>
            </div>
          </>
        );
      case "forgot":
        return (
          <>
            <div className="mt-1 flex flex-col flex-1 gap-4">
              <Text
                size={15}
                element="p"
                leading={1.5}
                color="black90"
                className="mb-0"
              >
                Enter your email address and we’ll send you a link to create a
                new password.
              </Text>
              <Textbox
                id="email"
                type="email"
                value={email}
                placeholder="Enter your email address"
                onChange={(e) => setEmail(e)}
              />
              <Button
                onClick={async () => {
                  // get the root url
                  //url encode the email
                  setLoading(true);

                  let url = `${win.location.origin}/password-reset`;

                  const response = await invoke("passwordReset", {
                    email_address: email,
                    agent_id: process.env.GATSBY_AGENT_ID,
                    redirectTo: url,
                  });
                  if (response.status !== 200) {
                    return;
                  }

                  onModeChange("check");
                  setLoading(false);
                }}
                size="large"
                loading={loading}
                disabled={!isForgotValid}
                color="green"
                width="full"
                text="Send Instructions"
                className="!m-0"
              />
            </div>{" "}
          </>
        );
      case "check":
        return (
          <>
            <div className="mt-1 flex flex-col flex-1 gap-2">
              <div>
                <Text
                  size={15}
                  element="p"
                  leading={1.5}
                  color="black90"
                  className="mb-3"
                >
                  If a {process.env.GATSBY_SITE_TITLE} account exists for{" "}
                  <span className="font-semibold">{email}</span>, an email will
                  be sent with further instructions.
                </Text>
                <Text size={15} element="p" leading={1.5} color="black90">
                  If you do not receive an email, please check your Spam folder
                  or contact us via{" "}
                  <a
                    href="mailto:support@golfsavers.com"
                    className="font-semibold"
                  >
                    support@golfsavers.com
                  </a>
                  .
                </Text>
              </div>
              <Button
                onClick={async () => {
                  hideModals();
                }}
                size="large"
                color="green"
                width="full"
                text="Close"
                className="!m-0"
              />
            </div>{" "}
          </>
        );
      case "home":
      case "manage":
      case "checkout":
        return (
          <>
            <div className="flex flex-col gap-[10px]">
              <div
                onClick={async () => {
                  clearError();
                  supabase.auth.signInWithOAuth({
                    provider: "google",
                    options: {
                      redirectTo: getRedirectUrl,
                    },
                  });
                }}
                className="flex items-center justify-center cursor-pointer bg-white border border-grey3 rounded-md shadow-sm p-[10px] font-semibold gap-[10px] text-black/90 text-17"
              >
                <svg
                  className="w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  width="705.6"
                  height="720"
                  viewBox="0 0 186.69 190.5"
                >
                  <g transform="translate(1184.583 765.171)">
                    <path
                      clipPath="none"
                      mask="none"
                      d="M-1089.333-687.239v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z"
                      fill="#4285f4"
                    />
                    <path
                      clipPath="none"
                      mask="none"
                      d="M-1142.714-651.791l-6.972 5.337-24.679 19.223h0c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z"
                      fill="#34a853"
                    />
                    <path
                      clipPath="none"
                      mask="none"
                      d="M-1174.365-712.61c-6.494 12.815-10.217 27.276-10.217 42.689s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z"
                      fill="#fbbc05"
                    />
                    <path
                      d="M-1089.333-727.244c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276c-16.539-15.413-38.013-24.852-63.731-24.852-37.234 0-69.359 21.388-85.032 52.561l31.692 24.592c7.533-22.514 28.575-39.226 53.34-39.226z"
                      fill="#ea4335"
                      clipPath="none"
                      mask="none"
                    />
                  </g>
                </svg>
                Continue with Google
              </div>

              <div
                onClick={() => {
                  supabase.auth.signInWithOAuth({
                    provider: "facebook",
                    options: {
                      redirectTo: getRedirectUrl,
                    },
                  });
                }}
                className="flex items-center justify-center cursor-pointer bg-white border border-grey3 rounded-md shadow-sm p-[10px] font-semibold gap-[10px] text-black/90 text-17"
              >
                <svg
                  className="w-6 h-6"
                  xmlns="http://www.w3.org/2000/svg"
                  width="1365.12"
                  height="1365.12"
                  viewBox="0 0 14222 14222"
                >
                  <circle cx="7111" cy="7112" r="7111" fill="#1977f3" />
                  <path
                    d="M9879 9168l315-2056H8222V5778c0-562 275-1111 1159-1111h897V2917s-814-139-1592-139c-1624 0-2686 984-2686 2767v1567H4194v2056h1806v4969c362 57 733 86 1111 86s749-30 1111-86V9168z"
                    fill="#fff"
                  />
                </svg>
                Continue with Facebook
              </div>
              <div
                onClick={() => {
                  supabase.auth.signInWithOAuth({
                    provider: "linkedin_oidc",
                    options: {
                      redirectTo: getRedirectUrl,
                    },
                  });
                }}
                className="flex items-center justify-center cursor-pointer bg-white border border-grey3 rounded-md shadow-sm p-[10px] font-semibold gap-[10px] text-black/90 text-17"
              >
                <svg
                  className="w-6 h-6"
                  height="72"
                  viewBox="0 0 72 72"
                  width="72"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g fill="none" fillRule="evenodd">
                    <path
                      d="M8,72 L64,72 C68.418278,72 72,68.418278 72,64 L72,8 C72,3.581722 68.418278,-8.11624501e-16 64,0 L8,0 C3.581722,8.11624501e-16 -5.41083001e-16,3.581722 0,8 L0,64 C5.41083001e-16,68.418278 3.581722,72 8,72 Z"
                      fill="#007EBB"
                    />
                    <path
                      d="M62,62 L51.315625,62 L51.315625,43.8021149 C51.315625,38.8127542 49.4197917,36.0245323 45.4707031,36.0245323 C41.1746094,36.0245323 38.9300781,38.9261103 38.9300781,43.8021149 L38.9300781,62 L28.6333333,62 L28.6333333,27.3333333 L38.9300781,27.3333333 L38.9300781,32.0029283 C38.9300781,32.0029283 42.0260417,26.2742151 49.3825521,26.2742151 C56.7356771,26.2742151 62,30.7644705 62,40.051212 L62,62 Z M16.349349,22.7940133 C12.8420573,22.7940133 10,19.9296567 10,16.3970067 C10,12.8643566 12.8420573,10 16.349349,10 C19.8566406,10 22.6970052,12.8643566 22.6970052,16.3970067 C22.6970052,19.9296567 19.8566406,22.7940133 16.349349,22.7940133 Z M11.0325521,62 L21.769401,62 L21.769401,27.3333333 L11.0325521,27.3333333 L11.0325521,62 Z"
                      fill="#FFF"
                    />
                  </g>
                </svg>
                Continue with LinkedIn
              </div>
            </div>
            <div className="inline-flex relative items-center justify-center w-full">
              <hr className="w-full h-px my-8 bg-grey3 border-0" />
              <span className="text-15 absolute px-3 italic text-black/40 -translate-x-1/2 bg-white left-1/2">
                or
              </span>
            </div>
            <div className="mt-1 flex flex-col flex-1 gap-5">
              <Textbox
                id="email"
                value={email}
                type="email"
                error={emailError}
                placeholder="Email Address"
                onChange={(e) => setEmail(e)}
                onBlur={() => {
                  if (!email) {
                    setEmailError("Please enter your email address");
                  } else if (!isValidEmail) {
                    setEmailError("Please enter a valid email address");
                  } else {
                    setEmailError("");
                  }
                }}
              />

              <Button
                onClick={async () => {
                  validateSignForm();
                  if (!email) return;
                  if (!isCheckoutValid) return;

                  setLoading(true);
                  let response = await checkEmailExistsAndProvider(email);
                  // check if there is only an email provider
                  let provider = getProvider(response?.providers);
                  if (provider) {
                    clearError();
                    if (provider === "email") {
                      onModeChange("login");
                    } else {
                      onModeChange(provider);
                    }
                  } else {
                    onModeChange("signup");
                  }
                  setLoading(false);
                }}
                loading={loading}
                size="large"
                color="green"
                width="full"
                text="Continue with email"
              />
            </div>
            {mode === "checkout" ? (
              <>
                <div className="inline-flex items-center relative justify-center w-full">
                  <hr className="w-full h-px my-8 bg-grey3 border-0" />
                  <span className="text-15 absolute px-3 italic text-black/40 -translate-x-1/2 bg-white left-1/2">
                    or
                  </span>
                </div>
                <div className="mt-1 flex flex-col flex-1 gap-4">
                  <Button
                    onClick={() => {
                      navigate("/checkout");
                    }}
                    size="large"
                    invert
                    color="green"
                    width="full"
                    text="Continue as guest"
                    className="!m-0"
                  />
                  <Text
                    size={11}
                    color="black90"
                    leading={1.3}
                    className="text-center mt-2"
                  >
                    By continuing, you agree to the Golfsavers{" "}
                    <a
                      href={`/terms-and-conditions`}
                      onClick={() => {
                        hideModals();
                      }}
                      target="_blank"
                      className="underline"
                    >
                      Terms and Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href={`/privacy-policy`}
                      onClick={() => {
                        hideModals();
                      }}
                      target="_blank"
                      className="underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </Text>
                </div>
              </>
            ) : (
              <div className="mt-0 flex flex-col flex-1 gap-4">
                <Text
                  size={11}
                  color="black90"
                  leading={1.3}
                  className="text-center"
                >
                  By continuing, you agree to the Golfsavers{" "}
                  <a
                    href={`/terms-and-conditions`}
                    onClick={() => {
                      hideModals();
                    }}
                    target="_blank"
                    className="underline"
                  >
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href={`/privacy-policy`}
                    onClick={() => {
                      hideModals();
                    }}
                    target="_blank"
                    className="underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </Text>
              </div>
            )}{" "}
          </>
        );
      default:
        return <> </>;
    }
  };

  return <>{renderMode()}</>;
};

export default AuthUI;
